import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Translate} from 'react-redux-i18n';
import * as notificationsActions from '../../../../../actions/notifications';
import * as snackbarsActions from '../../../../../actions/snackbars';
import {captureError} from '../../../../../utils/log';
import authenticationService from '../../../../../services/authentication/AuthenticationService';
import subscriptionService from '../../../../../services/subscription/SubscriptionService';
import {notificationType} from '../../../../../utils/constant';
import InvitationsView from './InvitationsView';
import relationshipService from '../../../../../services/relationship/RelationshipService';
import interventionService from '../../../../../services/intervention/InterventionService';
import userService from '../../../../../services/user/UserService';
import notificationService from '../../../../../services/notification/NotificationService';
import InvitationRejectionReason from '../../../../InvitationRejectionReason';
import {useCustomStore} from '../CollectorSectionStore';
import {goToProject} from '../../../../../utils/project';

function InvitationsContainer() {
  const [organisation, invitations] = useSelector(globalState => [
    globalState.organisation,
    globalState.notifications.notificationsWithActions.filter(({type}) => type !== notificationType.DOCUMENT_AT_DATE)
  ]);
  const globalDispatch = useDispatch();
  const {dispatch} = useCustomStore();
  const isBackOffice = authenticationService.roles().isBackOffice();
  const [canAddUser, setCanAddUser] = useState(isBackOffice);
  const [invitationToReject, setInvitationToReject] = useState(null);

  function refreshInvitations() {
    globalDispatch(notificationsActions.fetchNotifications(organisation));
  }

  React.useEffect(() => {
    async function checkUserAdding() {
      try {
        const subscription = await subscriptionService.findSubscriptionByOrganisation(organisation.organisationId);
        setCanAddUser(subscription ?
          (subscription.maximumNumberOfUserAccounts - subscription.numberOfUserAccounts > 0) :
          false);
      } catch (error) {
        captureError('Error while checking if organisation\'s subscription has some user slot left', error);
      }
    }

    // If user is not BO, we need to check organisation's subscription to see if a user slot is still available
    if (!isBackOffice && invitations.some(invitation => invitation.type === notificationType.USER_PENDING)) {
      setCanAddUser(false);
      checkUserAdding();
    }
  }, [invitations.length]);

  React.useEffect(() => {
    refreshInvitations();
  }, []);

  function dismissNotification(id) {
    globalDispatch(notificationsActions.dismissNotification(id));
  }

  function handleAccept(invitation) {
    function accept(service, successFunction) {
      service()
        .then(successFunction)
        .catch(error => {
          refreshInvitations();
          captureError(`Error while accepting invitation of type ${invitation.type} in dashboard`, error);
          globalDispatch(snackbarsActions.addError(<Translate value="error.generic"/>));
        });
    }

    dismissNotification(invitation.notificationId);

    switch (invitation.type) {
      case notificationType.RELATIONSHIP_NOT_AUTOMATICALLY_ACCEPTED:
        accept(
          () => relationshipService.accept(invitation.relationship.relationshipId),
          () => dispatch({type: 'FETCH_SUBCONTRACTORS'})
        );
        break;
      case notificationType.PROJECT_NOT_AUTOMATICALLY_ACCEPTED:
        accept(
          () => interventionService.accept(invitation.intervention.interventionId),
          () => goToProject(invitation.intervention.projectId, invitation.intervention.interventionId)
        );
        break;
      case notificationType.USER_PENDING:
        accept(
          () => userService.linkOrganisationToUsers(organisation.organisationId, [{id: invitation.user.userId}]),
          () => {
            notificationService.dismiss(invitation.notificationId);
            dispatch({type: 'FETCH_USERS'});
          }
        );
        break;
      default:
        captureError('Unknown notification type');
        break;
    }
  }

  function handleReject(invitation) {
    if (invitation.type === notificationType.USER_PENDING) {
      dismissNotification(invitation.notificationId);
      notificationService.dismiss(invitation.notificationId);
      return;
    }

    setInvitationToReject(invitation);
  }

  function handleRejectionSuccess() {
    dismissNotification(invitationToReject.notificationId);
    setInvitationToReject(null);
  }

  function handleRejectionError() {
    refreshInvitations();
    setInvitationToReject(null);
  }

  return (
    <>
      <InvitationsView
        canAddUser={canAddUser}
        invitations={invitations}
        onAccept={handleAccept}
        onReject={handleReject}
      />
      {invitationToReject &&
        <InvitationRejectionReason
          onClose={() => setInvitationToReject(null)}
          invitation={invitationToReject}
          onSubmitSuccess={handleRejectionSuccess}
          onSubmitError={handleRejectionError}
        />
      }
    </>
  );
}

export default InvitationsContainer;
